import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

export interface EventYear {
  id: string
  year: number
  event_name: string
  start_date: string | null
  end_date: string | null
  is_archived: boolean
  press_active: boolean
  created_at: string
  updated_at: string
}

interface EventYearContextType {
  currentYear: number
  setCurrentYear: (year: number) => void
  availableYears: EventYear[]
  refreshYears: () => Promise<void>
  canSwitchYears: boolean
}

const EventYearContext = createContext<EventYearContextType | undefined>(undefined)

export const EventYearProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [availableYears, setAvailableYears] = useState<EventYear[]>([])
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear())

  const canSwitchYears = user?.role === 'pr' || user?.role === 'admin'

  const loadYears = async () => {
    try {
      const { data, error } = await supabase
        .from('event_settings')
        .select('*')
        .order('year', { ascending: false })

      if (error) throw error

      if (data && data.length > 0) {
        setAvailableYears(data)

        const activeYear = data.find(y => !y.is_archived)
        if (activeYear) {
          if (canSwitchYears) {
            const saved = localStorage.getItem('fx-selected-year')
            if (saved && data.some(y => y.year === Number(saved))) {
              setCurrentYear(Number(saved))
            } else {
              setCurrentYear(activeYear.year)
            }
          } else {
            setCurrentYear(activeYear.year)
          }
        }
      }
    } catch (error) {
      console.error('Error loading event years:', error)
    }
  }

  useEffect(() => {
    loadYears()
  }, [user])

  const handleSetCurrentYear = (year: number) => {
    if (!canSwitchYears) return
    setCurrentYear(year)
    localStorage.setItem('fx-selected-year', String(year))
  }

  const refreshYears = async () => {
    await loadYears()
  }

  return (
    <EventYearContext.Provider value={{
      currentYear,
      setCurrentYear: handleSetCurrentYear,
      availableYears,
      refreshYears,
      canSwitchYears
    }}>
      {children}
    </EventYearContext.Provider>
  )
}

export const useEventYear = () => {
  const context = useContext(EventYearContext)
  if (context === undefined) {
    throw new Error('useEventYear must be used within an EventYearProvider')
  }
  return context
}
